// src/pages/Dashboard/Dashboard.js

import React, { useState } from "react";
import FullScreenSpinner from "../../components/FullScreenSpinner";
import {
  Box, Typography, Paper, Stack, Button, IconButton, Grid,
  Divider, FormControl, Select, MenuItem, Checkbox,
  ListItemText, TextField, Chip, List, ListItem, ListItemButton,
  ListSubheader, InputAdornment
} from "@mui/material";
import {
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import useDashboardData from "../../hooks/useDashboardData";
import SearchableSelect from "../../components/Common/SearchableSelect";

import {
  AgeCell,
  AgeRow,
  CardPaper,
  ContentInner,
  DashboardShell,
  LegendDot,
  LegendItem,
  LegendList,
  LeftPanel,
  MainHeader,
  MainHeaderLeft,
  MainHeaderRight,
  MainPanel,
  MainScroll,
  MfBarRow,
  MfBarTitleRow,
  MfBarTrack,
  MfSegment,
  RightPanel,
} from "./Dashboard.styles";

// Iconos para el sidebar
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";

const ICON_MAP = {
  PeopleIcon: <PeopleIcon fontSize="small" />,
  BarChartIcon: <BarChartIcon fontSize="small" />,
  AssignmentIcon: <AssignmentIcon fontSize="small" />,
  AssignmentTurnedInIcon: <AssignmentIcon fontSize="small" />,
};

const COLORS = ["#e3dfff", "#9912d7", "#000000", "#3880ee", "#1054ad", "#f02ca1"];

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null;

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={{ fontSize: '12px', fontWeight: 'bold', textShadow: '0px 1px 2px rgba(0,0,0,0.3)' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const Dashboard = () => {
  const [isLeftOpen, setIsLeftOpen] = useState(true);
  const [isRightOpen, setIsRightOpen] = useState(true);
  const [searchTermFiltros, setSearchTermFiltros] = useState({});

  const {
    config,
    activeTab,
    setActiveTab,
    loadingConfig,
    filtrosValores,
    catalogos,
    loadingIndicadores,
    gruposEdadesChartData,
    gruposRiesgoData,
    totalRiesgo,
    totalPersonas,
    totalMujeres,
    totalHombres,
    aEdadesData,
    aCohorteData,
    handleFiltroChange,
    aplicarFiltros,
    limpiarFiltros
  } = useDashboardData();

  const [expandedGrupos, setExpandedGrupos] = useState({});

  if (loadingConfig) return <FullScreenSpinner text="Cargando configuraciÃ³n..." />;

  if (!config || config.length === 0) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "80vh" }}>
        <Typography variant="h6" color="text.secondary">No se encontrÃ³ la configuraciÃ³n del Dashboard.</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>Reintentar</Button>
      </Box>
    );
  }

  const seccionActiva = config.find(s => s.clave_tecnica === activeTab);

  // --- RENDERS DE GRÃFICAS ---

  const renderKPIs = () => (
    <Box sx={{ display: "flex", width: "100%", gap: 3, mb: 3 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CardPaper elevation={0} sx={{ p: 2, height: "100%" }}>
          <Typography variant="caption" sx={{ color: "#718096", fontWeight: 700, textTransform: "uppercase" }}>Total Paciente</Typography>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#9413b8", mt: 1 }}>{totalPersonas.toLocaleString()}</Typography>
        </CardPaper>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CardPaper elevation={0} sx={{ p: 2, height: "100%" }}>
          <Typography variant="caption" sx={{ color: "#718096", fontWeight: 700, textTransform: "uppercase" }}>Total Femenino</Typography>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#f02ca0", mt: 1 }}>{totalMujeres.toLocaleString()}</Typography>
        </CardPaper>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CardPaper elevation={0} sx={{ p: 2, height: "100%" }}>
          <Typography variant="caption" sx={{ color: "#718096", fontWeight: 700, textTransform: "uppercase" }}>Total Masculino</Typography>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#234f95", mt: 1 }}>{totalHombres.toLocaleString()}</Typography>
        </CardPaper>
      </Box>
    </Box>
  );

  const renderPiramide = () => (
    <Box sx={{ display: "flex", width: "100%", gap: 2, alignItems: "stretch" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CardPaper elevation={0} sx={{ p: 2, height: "100%" }}>
          <MfBarTitleRow>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#2d3748" }}>
              Grupo de edades
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <LegendDot $color="#1054ad" />
                <Typography variant="caption" sx={{ color: "#475569", fontWeight: 700 }}>M</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <LegendDot $color="#f02ca0" />
                <Typography variant="caption" sx={{ color: "#475569", fontWeight: 700 }}>F</Typography>
              </Box>
              <Typography variant="caption" sx={{ color: "#475569", fontWeight: 700, ml: 1 }}>
                Total
              </Typography>
            </Box>
          </MfBarTitleRow>

          <MfBarRow style={{ paddingTop: 6, paddingBottom: 6, borderBottom: "1px solid #eef2f7" }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800 }}>GRUPO</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, width: 50, textAlign: "right" }}>TOTAL</Typography>
          </MfBarRow>

          {gruposEdadesChartData.map((row) => {
            const m = Number(row.M || 0);
            const f = Number(row.F || 0);
            const total = m + f || 1;
            const mPct = (m / total) * 100;
            const fPct = (f / total) * 100;

            return (
              <MfBarRow key={row.name}>
                <Typography sx={{ width: 95, flexShrink: 0, fontSize: "12px", color: "#0f172a", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {row.name}
                </Typography>
                <MfBarTrack>
                  <MfSegment $bg="#1054ad" style={{ width: `${mPct}%` }}>
                    {mPct >= 15 && m.toLocaleString()}
                  </MfSegment>
                  <MfSegment $bg="#f02ca0" style={{ width: `${fPct}%` }}>
                    {fPct >= 15 && f.toLocaleString()}
                  </MfSegment>
                </MfBarTrack>
                <Typography sx={{ width: 50, flexShrink: 0, textAlign: "right", fontSize: "12px", color: "#0f172a", fontWeight: 700 }}>
                  {Number(row.Total || 0).toLocaleString()}
                </Typography>
              </MfBarRow>
            );
          })}
        </CardPaper>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <CardPaper elevation={0} sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#2d3748", mb: 1 }}>
            Grupo de edades
          </Typography>

          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flexShrink: 0 }}>
              {gruposEdadesChartData.map((item, index) => (
                <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LegendDot $color={COLORS[index % COLORS.length]} />
                  <Typography sx={{ fontSize: "11px", color: "#64748b", whiteSpace: "nowrap" }}>{item.name}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", minWidth: 0 }}>
              <PieChart width={350} height={300}>
                <Pie
                  data={gruposEdadesChartData}
                  dataKey="Total"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  label={renderCustomizedLabel}
                  labelLine={false}
                  paddingAngle={1}
                >
                  {gruposEdadesChartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </Box>
          </Box>
        </CardPaper>
      </Box>
    </Box>
  );

  const renderCohorte = () => (
    <Paper elevation={0} sx={{ p: 3, flex: 1, bgcolor: "#fff", borderRadius: "12px", border: "1px solid #edf2f7", overflowY: "auto" }}>
      <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">Análisis de Cohorte</Typography>
        <Typography variant="h6" fontWeight="bold" color="#94a3b8">Total: {totalRiesgo.toLocaleString()}</Typography>
      </Stack>
      {gruposRiesgoData.map(grupo => {
        const pct = totalRiesgo > 0 ? (grupo.total / totalRiesgo) * 100 : 0;
        const isExpanded = !!expandedGrupos[grupo.id];
        return (
          <Box key={grupo.id} >
            <Box onClick={() => setExpandedGrupos(prev => ({ ...prev, [grupo.id]: !prev[grupo.id] }))} sx={{ display: "flex", alignItems: "center", p: 1.5, cursor: "pointer", "&:hover": { bgcolor: "#f7fafc" }, borderRadius: "8px" }}>
              <Typography sx={{ width: 30, color: "#a0aec0", fontWeight: "bold" }}>{grupo.subgrupos.length > 0 ? (isExpanded ? "-" : "+") : "-"}</Typography>
              <Typography sx={{ flex: 1, fontSize: "14px", fontWeight: 600, color: "#2d3748" }}>{grupo.name}</Typography>
              <Typography sx={{ width: 100, textAlign: "right", fontWeight: "bold", mr: 3, color: "#2c3e50" }}>{grupo.total.toLocaleString()}</Typography>
              <Box sx={{ flex: 1.5, height: 10, bgcolor: "#edf2f7", borderRadius: 5, overflow: "hidden" }}>
                <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: "#9912d7", transition: "width 0.8s ease" }} />
              </Box>
              <Typography sx={{ width: 40, textAlign: "right", fontSize: "11px", ml: 1, color: "#718096" }}>{pct.toFixed(0)}%</Typography>
            </Box>
            {isExpanded && grupo.subgrupos.map(sub => (
              <Box key={sub.name} sx={{ pl: 6, py: 0.8, display: "flex", alignItems: "center" }}>
                <Typography sx={{ flex: 1, fontSize: "13px", color: "#4a5568" }}>{sub.name}</Typography>
                <Typography sx={{ width: 100, textAlign: "right", fontSize: "13px", mr: 3, color: "#718096" }}>{sub.value.toLocaleString()}</Typography>
                <Box sx={{ flex: 1.5, height: 6, bgcolor: "#f7fafc", borderRadius: 3, overflow: "hidden" }}>
                  <Box sx={{ width: `${(sub.value / grupo.total) * 100}%`, height: "100%", bgcolor: "#e2b2f8ff" }} />
                </Box>
                <Box sx={{ width: 40 }} />
              </Box>
            ))}
          </Box>
        );
      })}
    </Paper>
  );

  const renderActividades = (data, title) => (
    <Paper elevation={0} sx={{ p: 3, flex: 1, bgcolor: "#fff", borderRadius: "4px", border: "1px solid #edf2f7", overflowY: "auto" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>{title}</Typography>
      <Box sx={{ display: "flex", mb: 2, px: 1 }}>
        <Typography variant="caption" sx={{ flex: 2, fontWeight: "bold", color: "#718096" }}>Actividad</Typography>
        <Typography variant="caption" sx={{ flex: 1.5, fontWeight: "bold", textAlign: "right", color: "#718096" }}>Masculino (Realizado %)</Typography>
        <Typography variant="caption" sx={{ flex: 1.5, fontWeight: "bold", textAlign: "right", color: "#718096" }}>Femenino (Realizado %)</Typography>
      </Box>
      {data.map((row, i) => {
        const pctM = (row.M.realizadas / (row.M.realizadas + row.M.pendientes || 1)) * 100;
        const pctF = (row.F.realizadas / (row.F.realizadas + row.F.pendientes || 1)) * 100;
        return (
          <Box key={i} sx={{ display: "flex", alignItems: "center", p: 1.5, borderBottom: "1px solid #f1f5f9" }}>
            <Typography sx={{ flex: 2, fontSize: "13px", color: "#2d3748", fontWeight: 500 }}>{row.name}</Typography>
            <Box sx={{ flex: 1.5, px: 2 }}>
              <Typography variant="caption" display="block" textAlign="right" sx={{ mb: 0.5 }}>{row.M.realizadas} / {row.M.realizadas + row.M.pendientes} ({pctM.toFixed(0)}%)</Typography>
              <Box sx={{ height: 8, bgcolor: "#5B9BD515", borderRadius: 4, overflow: "hidden" }}>
                <Box sx={{ width: `${pctM}%`, height: "100%", bgcolor: "#1054ad" }} />
              </Box>
            </Box>
            <Box sx={{ flex: 1.5, px: 2 }}>
              <Typography variant="caption" display="block" textAlign="right" sx={{ mb: 0.5 }}>{row.F.realizadas} / {row.F.realizadas + row.F.pendientes} ({pctF.toFixed(0)}%)</Typography>
              <Box sx={{ height: 8, bgcolor: "#ED7D3115", borderRadius: 4, overflow: "hidden" }}>
                <Box sx={{ width: `${pctF}%`, height: "100%", bgcolor: "#f02ca0" }} />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Paper>
  );

  const renderFiltroField = (f) => {
    const catalogData = catalogos[f.id] || [];
    const keyVal = (f.nombre_campo === 'edadIni' || f.nombre_campo === 'edadFin') ? f.nombre_campo : f.clave_vinculacion;
    const isMultiple = f.tipo_control === 'select_multiple';

    return (
      <Box key={f.id} sx={{ mb: 1.5 }}>
        <Typography component="label" sx={{ display: "block", fontSize: "11px", color: "#64748b", fontWeight: "500", mb: "3px", fontFamily: "var(--font-body)" }}>
          {f.label}
        </Typography>
        <FormControl fullWidth size="small">
          {f.tipo_control && f.tipo_control.startsWith('select') ? (
            <SearchableSelect
              multiple={isMultiple}
              options={catalogData}
              value={filtrosValores[keyVal] ?? (isMultiple ? [] : "")}
              onValueChange={(newVal) => handleFiltroChange(keyVal, newVal)}
              placeholder="Seleccione..."
              getOptionValue={(item) => String(item.id ?? item.eps ?? item.codigo ?? "")}
              getOptionLabel={(item) => String(item.descripcion || item.eps || item.nombre || item.label || item.codigo || "")}
              sx={{
                bgcolor: "#fff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  fontSize: "11px",
                  minHeight: "32px",
                  height: "32px",
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#94a3b8" },
              }}
              selectSx={{
                height: "32px",
                minHeight: "32px",
                fontSize: "11px",
                fontFamily: "var(--font-body)",
                "& .MuiSelect-select": {
                  padding: "4px 8px !important",
                  fontSize: "11px",
                  fontFamily: "var(--font-body)",
                },
              }}
            />
          ) : (
            <TextField
              fullWidth
              placeholder={f.nombre_campo.includes('edad') ? "Ej: 18" : "Escriba..."}
              type={f.tipo_control}
              size="small"
              value={filtrosValores[(f.nombre_campo === 'edadIni' || f.nombre_campo === 'edadFin') ? f.nombre_campo : f.clave_vinculacion] || ""}
              onChange={(e) => handleFiltroChange((f.nombre_campo === 'edadIni' || f.nombre_campo === 'edadFin') ? f.nombre_campo : f.clave_vinculacion, e.target.value)}
              sx={{
                bgcolor: "#fff",
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: "11px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                "& .MuiInputBase-input": { padding: "4px 8px !important" }
              }}
            />
          )}
        </FormControl>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DashboardShell $leftOpen={isLeftOpen} $rightOpen={isRightOpen}>
        {isLeftOpen && (
          <LeftPanel>
            <Box sx={{ p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#2d3748", fontSize: "13px" }}>Secciones</Typography>
              <IconButton size="small" onClick={() => setIsLeftOpen(false)}><ChevronLeftIcon sx={{ fontSize: 18 }} /></IconButton>
            </Box>
            <Divider />
            <List dense sx={{ mt: 0.5 }}>
              {config.map((s) => (
                <ListItem key={s.clave_tecnica} disablePadding>
                  <ListItemButton
                    selected={activeTab === s.clave_tecnica}
                    onClick={() => setActiveTab(s.clave_tecnica)}
                    sx={{
                      py: 0.8,
                      px: 1.5,
                      mx: 0.5,
                      borderRadius: "6px",
                      mb: 0.3,
                      minHeight: 0,
                      "&.Mui-selected": { bgcolor: "rgba(26, 115, 232, 0.08)", color: "primary.main" },
                    }}
                  >
                    <ListItemText primary={s.nombre} primaryTypographyProps={{ fontSize: "12px", fontWeight: activeTab === s.clave_tecnica ? 700 : 500, noWrap: true }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </LeftPanel>
        )}

        <MainPanel>
          <MainHeader $hidden={isLeftOpen && isRightOpen}>
            <MainHeaderLeft>
              {!isLeftOpen && (
                <IconButton size="small" onClick={() => setIsLeftOpen(true)}>
                  <MenuIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </MainHeaderLeft>
            <MainHeaderRight>
              {!isRightOpen && (
                <IconButton size="small" onClick={() => setIsRightOpen(true)}>
                  <FilterListIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </MainHeaderRight>
          </MainHeader>

          <MainScroll>
            {loadingIndicadores ? (
              <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
                <FullScreenSpinner />
                <Typography sx={{ color: "#A3AED0", fontWeight: 600 }}>Sincronizando datos en tiempo real...</Typography>
              </Box>
            ) : (
              <ContentInner>
                {activeTab === "piramide" && (
                  <>
                    {renderKPIs()}
                    {renderPiramide()}
                  </>
                )}
                {activeTab === "cohorte" && renderCohorte()}
                {activeTab === "act_edades" && renderActividades(aEdadesData, "Actividades por Grupos de Edades")}
                {activeTab === "act_cohorte" && renderActividades(aCohorteData, "Actividades por Cohorte")}
              </ContentInner>
            )}
          </MainScroll>
        </MainPanel>

        {isRightOpen && (
          <RightPanel>
            <Box sx={{ p: 1, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Typography variant="subtitle2" sx={{ fontSize: "12px", fontWeight: "600", color: "#334155" }}>Filtros</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: "5px",
                    textTransform: "none",
                    fontSize: "11px",
                    height: 26,
                    px: 1.5,
                    color: "#64748b",
                    borderColor: "#cbd5e1",
                    fontWeight: "600",
                    bgcolor: "#f1f5f9",
                    "&:hover": { bgcolor: "#e2e8f0", borderColor: "#94a3b8" },
                  }}
                  onClick={limpiarFiltros}
                >
                  Limpiar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  sx={{
                    borderRadius: "5px",
                    textTransform: "none",
                    fontSize: "11px",
                    height: 26,
                    px: 1.5,
                    bgcolor: "#22c55e",
                    fontWeight: "700",
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#16a34a", boxShadow: "none" },
                  }}
                  onClick={aplicarFiltros}
                >
                  Aplicar
                </Button>
                <IconButton size="small" onClick={() => setIsRightOpen(false)}><ChevronLeftIcon sx={{ transform: "rotate(180deg)", fontSize: "16px" }} /></IconButton>
              </Stack>
            </Box>
            <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
              {seccionActiva?.filtros.reduce((acc, f, i, arr) => {
                if (f.nombre_campo === "edadIni") {
                  const next = arr[i + 1];
                  if (next && next.nombre_campo === "edadFin") {
                    acc.push(
                      <Grid container spacing={1} key="row-edades" sx={{ mb: 1 }}>
                        <Grid item xs={6}>
                          <Typography component="label" sx={{ display: "block", fontSize: "10px", color: "#64748b", fontWeight: "500", mb: "2px" }}>Edad Min</Typography>
                          <TextField fullWidth placeholder="Ej: 18" type="number" size="small" value={filtrosValores[f.nombre_campo] || ""} onChange={(e) => handleFiltroChange(f.nombre_campo, e.target.value)} sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: "11px" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" }, "& .MuiInputBase-input": { padding: "4px 6px !important" } }} />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography component="label" sx={{ display: "block", fontSize: "10px", color: "#64748b", fontWeight: "500", mb: "2px" }}>Edad Max</Typography>
                          <TextField fullWidth placeholder="Ej: 65" type="number" size="small" value={filtrosValores[next.nombre_campo] || ""} onChange={(e) => handleFiltroChange(next.nombre_campo, e.target.value)} sx={{ bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: "11px" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" }, "& .MuiInputBase-input": { padding: "4px 6px !important" } }} />
                        </Grid>
                      </Grid>
                    );
                    return acc;
                  }
                }
                if (f.nombre_campo === "edadFin") return acc;
                acc.push(renderFiltroField(f));
                return acc;
              }, [])}
            </Box>
          </RightPanel>
        )}
      </DashboardShell>
    </LocalizationProvider>
  );
};

export default Dashboard;
